'use client'

import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useTabPoll } from '../autoRefresh'
import { apiGet } from './entriesApi'
import { Chip } from './autoDomainChips'
import { STATUS_TONE, fmtAgo, fmtDuration, isStale, type JobRun, type RunsResponse } from './jobTypes'

// The brand's slice of the Jobs board, as a bento card on its Domain
// Candidates dashboard: is a worker doing something for this platform right
// now, what did the last one do, and one click to the full board narrowed to
// the brand. Reads the same two endpoints Administration › Jobs does
// (backend/routes/backoffice/jobs.ts), which are Editor-only — the caller
// only mounts this for an Editor, so a tenant never sees a 403 card.

const POLL_MS = 15000

function cardCls(isDark: boolean) {
  return `relative flex min-w-0 flex-col overflow-hidden border border-border bg-card`
}

// "analysing · 42/120 · example.com" — whichever of the worker's progress
// fields are filled in, in a fixed order, so the line reads the same for
// every job.
function progressLine(p: JobRun['progress']) {
  if (!p) return null
  const parts: string[] = []
  if (p.step) parts.push(p.step)
  if (p.done != null && p.total != null) parts.push(`${p.done}/${p.total}`)
  else if (p.done != null) parts.push(String(p.done))
  if (p.current) parts.push(p.current)
  else if (p.message) parts.push(p.message)
  // the pass's pace rides on the row too; the message already carries it
  // for an analysis, so only add it when nothing else said so
  else if (p.per_10m != null) parts.push(`${p.per_10m} / 10 min`)
  return parts.length ? parts.join(' · ') : null
}

export function BrandJobsCard({
  platformId,
  platformName,
  isDark,
  muted,
  text,
  className = 'h-full',
}: {
  platformId: number
  platformName: string | null
  isDark: boolean
  muted: string
  text: string
  className?: string
}) {
  const [open, setOpen] = useState<JobRun[]>([])
  const [last, setLast] = useState<JobRun | null>(null)
  const [error, setError] = useState<string | null>(null)
  // One poll at a time: a slow backend must not have the 15 s timer stack
  // requests behind each other.
  const inFlight = useRef(false)

  const fetchAll = useCallback(async () => {
    if (inFlight.current) return
    inFlight.current = true
    try {
      // /jobs/open is the whole queue (every platform, plus the platform-less
      // jobs); this card only wants the brand's own rows. History is already
      // newest-first, so page 0's head is the last run to finish.
      const [openRes, histRes] = await Promise.all([
        apiGet<JobRun[]>('/jobs/open'),
        apiGet<RunsResponse>(`/jobs/runs?platform_id=${platformId}&status=succeeded,failed,cancelled&latest=1`),
      ])
      if (!openRes.success || !histRes.success) {
        setError((openRes.success ? histRes.error : openRes.error) ?? 'jobs unavailable')
        return
      }
      setError(null)
      setOpen((openRes.data ?? []).filter((r) => r.platform_id === platformId))
      setLast(histRes.data?.runs[0] ?? null)
    } finally {
      inFlight.current = false
    }
  }, [platformId])

  // Live state, like the board itself: polled while the card is on screen so
  // a run's progress moves without a Refresh.
  useEffect(() => {
    fetchAll()
  }, [fetchAll])
  useTabPoll(`brand_jobs_${platformId}`, POLL_MS, fetchAll)

  const running = useMemo(() => open.filter((r) => r.status === 'running'), [open])
  const queued = useMemo(() => open.filter((r) => r.status === 'queued'), [open])
  const active = running[0] ?? null
  const stalled = active ? isStale(active) : false
  const progress = active ? progressLine(active.progress) : null

  const dotCls = active
    ? stalled ? 'bg-destructive' : 'animate-pulse bg-signal'
    : queued.length ? 'bg-warning' : 'bg-muted'

  return (
    <div className={`${cardCls(isDark)} ${className}`}>
      <div className="flex shrink-0 items-start justify-between gap-3 px-4 pt-3 pb-1">
        <div className={`font-mono text-[9px] uppercase tracking-widest ${muted}`}>Jobs · {platformName ?? 'this brand'}</div>
        <a href={`/entries/jobs?platform_id=${platformId}`} className={`shrink-0 font-mono text-[9px] uppercase tracking-widest ${muted} hover:text-signal`}>
          Jobs →
        </a>
      </div>

      <div className="flex min-h-0 flex-1 flex-col justify-between gap-2 px-4 pb-3">
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <span className={`h-2 w-2 shrink-0 rounded-full ${dotCls}`} />
            <span className={`truncate text-sm font-semibold tracking-tight ${text}`}>
              {active ? active.job_name : queued.length ? `${queued.length} queued` : 'Idle'}
            </span>
          </div>
          <div className={`mt-0.5 truncate font-mono text-[10px] ${muted}`} title={progress ?? undefined}>
            {active
              ? <>{stalled ? 'stalled' : 'running'} {fmtDuration(active.started_at)}{running.length > 1 && <> · +{running.length - 1} more</>}{queued.length > 0 && <> · {queued.length} queued</>}</>
              : queued.length
                ? <>{queued[0].job_name} · waiting for a worker</>
                : error ? <span className="text-destructive">{error}</span> : 'nothing running for this brand'}
          </div>
          {progress && <div className={`truncate font-mono text-[10px] ${muted}`}>{progress}</div>}
        </div>

        <div className={`flex min-w-0 items-center gap-2 font-mono text-[10px] ${muted}`}>
          <span className="shrink-0 uppercase tracking-widest text-[9px]">Last</span>
          {last ? (
            <>
              <span className={`truncate ${text}`} title={last.error ?? undefined}>{last.job_name}</span>
              <Chip label={last.status} tone={STATUS_TONE[last.status]} />
              <span className="shrink-0">{fmtAgo(last.finished_at ?? last.queued_at)}</span>
            </>
          ) : <span>no finished runs yet</span>}
        </div>
      </div>
    </div>
  )
}
